import type { Point, SubmissionGPSPoint } from "./yardSubmission";

type SavedYard = Record<string, any>;

export function loadYardConfiguration(data: SavedYard) {
    if (!data || typeof data !== "object") {
        throw new Error("Invalid yard configuration.");
    }

    const gpsPoints = data.gps_points;

    if (
        !Array.isArray(gpsPoints) ||
        gpsPoints.length !== 4 ||
        gpsPoints.some(
            (p: any) =>
                !p ||
                ![p.x, p.y, p.lat, p.lon].every(
                    (v: unknown) =>
                        typeof v === "number" &&
                        Number.isFinite(v)
                )
        )
    ) {
        throw new Error("Invalid GPS calibration points.");
    }

    const scale = data.scale;

    if (
        !scale ||
        !Number.isFinite(scale.meters_per_px) ||
        scale.meters_per_px <= 0
    ) {
        throw new Error("Invalid yard scale.");
    }

    const yard = data.yard ?? {};

    return {
        yardInfo: {
            name: String(yard.name ?? ""),
            address: String(yard.address ?? ""),
            zipCode: String(yard.zip_code ?? ""),
            altitudeA: String(yard.altitude_a ?? 30),
            altitudeB: String(yard.altitude_b ?? 50),
            altitudeC: String(yard.altitude_c ?? 2),
        },

        gpsPoints: gpsPoints.map(
            (p: SubmissionGPSPoint) => ({
                x: p.x,
                y: p.y,
                lat: p.lat,
                lon: p.lon,
            })
        ),

        geofencePoints: (
            data.geofence?.px ?? []
        ) as Point[],

        scalePxLength: Number(scale.px_length),
        scaleFeet: String(scale.feet),
        metersPerPx: Number(scale.meters_per_px),

        pod: data.pod?.home_px
            ? {
                px: data.pod.home_px as Point,
            }
            : null,

        imageWidth: Number(data.image?.width),
        imageHeight: Number(data.image?.height),
    };
}

export function loadYardGeometry(data: SavedYard) {
    const readArray = (value: unknown): any[] =>
        Array.isArray(value) ? value : [];

    return {
        sections: readArray(data.sections).map((s) => ({
            id: s.id,
            label: s.label,
            rect: {
                x: s.rect_px[0],
                y: s.rect_px[1],
                width: s.rect_px[2],
                height: s.rect_px[3],
            },
            entry: s.entry_px as Point,
            orientationDeg: s.orientation_deg,
        })),

        buildings: readArray(data.buildings).map((b) => ({
            id: b.id,
            label: b.label,
            points: b.polygon_px as Point[],
        })),

        scanners: readArray(data.scanners).map((s) => ({
            id: s.id,
            px: s.px as Point,
            orientationDeg: s.orientation_deg,
        })),

        overflowBlocks: readArray(data.blocks).map((b) => ({
            id: b.id,
            label: b.label,
            points: b.polygon_px as Point[],
            orientationDeg: b.orientation_deg,
            active: b.active,
        })),

        noParkingZones: readArray(data.no_parking_zones).map((z) => ({
            id: z.id,
            points: z.polygon_px as Point[],
        })),

        crosswalks: readArray(data.crosswalks).map((c) => ({
            id: c.id,
            p1: c.p1 as Point,
            p2: c.p2 as Point,
            widthFt: c.width_ft,
        })),
    };
}

export function loadParkingSpaces(
    data: SavedYard,
    metersPerPx: number
) {
    if (!Number.isFinite(metersPerPx) || metersPerPx <= 0) {
        throw new Error("Invalid parking scale.");
    }

    const spaces = data.parking_spaces;

    if (!Array.isArray(spaces)) {
        throw new Error("Saved parking spaces are missing.");
    }

    const pixelsPerFoot = 0.3048 / metersPerPx;

    return spaces.map((space: any, index: number) => {
        const c1 = space.corner1;
        const c2 = space.corner2;
        const angle = Number(space.angle);

        if (
            !Array.isArray(c1) ||
            !Array.isArray(c2) ||
            c1.length !== 2 ||
            c2.length !== 2 ||
            ![...c1, ...c2, angle].every(
                (v) => typeof v === "number" && Number.isFinite(v)
            )
        ) {
            throw new Error(`Invalid parking geometry at index ${index}.`);
        }

        const radians = angle * Math.PI / 180;

        const headingX = Math.sin(radians);
        const headingY = -Math.cos(radians);

        const sideX = Math.cos(radians);
        const sideY = Math.sin(radians);

        const dx = c1[0] - c2[0];
        const dy = c1[1] - c2[1];

        const lengthPx = dx * headingX + dy * headingY;
        const widthPx = dx * sideX + dy * sideY;

        if (lengthPx <= 0 || widthPx <= 0) {
            throw new Error(
                `Invalid parking dimensions at index ${index}.`
            );
        }

        const anchor: Point = [
            c1[0] - sideX * widthPx / 2,
            c1[1] - sideY * widthPx / 2,
        ];

        const lengthFt = lengthPx / pixelsPerFoot;
        const widthFt = widthPx / pixelsPerFoot;

        const backX = anchor[0] - headingX * lengthPx;
        const backY = anchor[1] - headingY * lengthPx;

        const reconstructedC2: Point = [
            backX - sideX * widthPx / 2,
            backY - sideY * widthPx / 2,
        ];

        if (
            Math.hypot(
                reconstructedC2[0] - c2[0],
                reconstructedC2[1] - c2[1]
            ) > 0.01
        ) {
            throw new Error(
                `Parking reconstruction failed at index ${index}.`
            );
        }

        return {
            id: String(space.id ?? ""),
            label: String(space.label ?? ""),
            anchor,
            angle,
            lengthFt,
            widthFt,
        };
    });
}
